// The refusal at dispatch (CR_20260921_Asset_Lifecycle L6 / L6a), where the dispatch was tried: the blocked assets in
// the reader's language (from the error's DETAIL, falling back to the server's own sentence), and — for a manager,
// when every block may be overridden — a required reason and "Dispatch anyway". Everyone else is told to renew the
// certificate or ask a manager; the server enforces the same (42501), this only mirrors it.
import { Alert, AlertTitle, Box, Button, Stack, TextField } from '@mui/material';
import { type DispatchBlock, dispatchBlock } from '@fleetspoke/core/lifecycle';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { calendarDate } from '../lib/time';

/** The refusal carried by a mutation error, or null for any other error. */
export function blockOf(error: unknown): DispatchBlock | null {
  return dispatchBlock(
    error as { code?: string; message?: string; details?: string; hint?: string } | null,
  );
}

export function DispatchBlockNotice({
  block,
  manager,
  pending,
  onOverride,
}: {
  block: DispatchBlock;
  manager: boolean;
  pending?: boolean;
  onOverride(reason: string): void;
}) {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState('');
  const lines = block.blockers.length
    ? block.blockers.map((b) =>
        b.reason === 'missing'
          ? t('dispatch.blocked_missing', { code: b.code, kind: b.kind })
          : b.reason === 'expired'
            ? t('dispatch.blocked_expired', {
                code: b.code,
                kind: b.kind,
                date: calendarDate(b.expires_on, i18n.language) ?? b.expires_on,
              })
            : t(`dispatch.blocked_${b.reason}`, {
                code: b.code,
                kind: b.kind,
                defaultValue: `${b.code} — ${b.reason}`,
              }),
      )
    : block.text.split('; ');
  return (
    <Alert severity="warning" data-testid="dispatch-blocked">
      <AlertTitle>{t('dispatch.blocked_title')}</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </Box>
      {!block.overridable ? (
        <Box sx={{ mt: 1 }}>{t('dispatch.not_overridable')}</Box>
      ) : manager ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mt: 1.5 }}
          alignItems={{ sm: 'flex-start' }}
        >
          <TextField
            size="small"
            fullWidth
            label={t('dispatch.override_reason')}
            helperText={t('dispatch.override_help')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'override-reason' } }}
          />
          <Button
            variant="contained"
            color="warning"
            disabled={!reason.trim() || pending}
            onClick={() => onOverride(reason.trim())}
            sx={{ flex: 'none', mt: { sm: 0.25 } }}
            data-testid="override-dispatch"
          >
            {t('dispatch.override')}
          </Button>
        </Stack>
      ) : (
        <Box sx={{ mt: 1 }}>{t('dispatch.ask_manager')}</Box>
      )}
    </Alert>
  );
}
